<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="!valid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' Usuario'"
      icon="mdi-account-multiple"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadUsers()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-form ref="form" lazy-validation v-model="valid">
              <!-- Personal Info Subtitle -->
              <v-row dense>
                <v-col cols="12">
                  <span class="subtitle-1">Datos personales:</span>
                </v-col>
              </v-row>
              <v-row>
                <!-- First Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.firstName"
                    label="Nombre"
                    counter="100"
                    maxlength="100"
                    :rules="[fieldRules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <!-- Last Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.lastName1"
                    label="Primer apellido"
                    counter="100"
                    maxlength="100"
                    :rules="[fieldRules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <!-- Last Name 2 -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.lastName2"
                    label="Segundo apellido"
                    counter="100"
                    maxlength="100"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <!-- Document ID -->
                <v-col cols="3">
                  <v-text-field
                    v-model="user.documentId"
                    label="Documento de identidad"
                    counter="100"
                    maxlength="100"
                    :rules="[fieldRules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <!-- Email -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.email"
                    label="Correo electrónico"
                    counter="100"
                    maxlength="100"
                    :rules="[fieldRules.emailFormat]"
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
              <!-- Divider -->
              <v-row dense>
                <v-col cols="12">
                  <v-divider></v-divider>
                </v-col>
              </v-row>
              <!-- System User Subtitle -->
              <v-row dense>
                <v-col cols="12">
                  <span class="subtitle-1">Usuario del sistema:</span>
                </v-col>
              </v-row>
              <v-row>
                <!-- Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.userName"
                    label="Nombre de usuario"
                    counter="32"
                    maxlength="32"
                    :rules="[fieldRules.required]"
                    outlined
                  ></v-text-field>
                </v-col>
                <!-- Password -->
                <v-col cols="4">
                  <v-text-field
                    v-model="user.password"
                    :type="showPassword ? 'text' : 'password'"
                    label="Contraseña"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    outlined
                    counter="30"
                    maxlength="30"
                    hint="Entre 6 y 30 caracteres. Debe contener al menos una mayúscula, una minúscula y un número."
                    persistent-hint
                    :rules="[validatePasswordRule]"
                    @click:append="showPassword = !showPassword"
                  ></v-text-field>
                </v-col>
                <!-- Actions -->
                <v-col cols="4">
                  <v-row no-gutters align="center" justify="space-between">
                    <!-- State -->
                    <v-col cols="8" class="text-right pb-1">
                      <span
                        class="subtitle-1"
                        @click="user.isActive = !user.isActive"
                        style="cursor: pointer"
                      >Activo:</span>
                    </v-col>
                    <v-col cols="3">
                      <v-switch v-model="user.isActive" :label="user.isActive?'Sí':'No'"></v-switch>
                    </v-col>
                    <!-- Password Change -->
                    <v-col cols="8" class="text-right pb-1">
                      <span
                        class="subtitle-1"
                        @click="user.needsPasswordUpdate = !user.needsPasswordUpdate"
                        style="cursor: pointer"
                      >Forzar cambio de contraseña:</span>
                    </v-col>
                    <v-col cols="3">
                      <v-switch
                        v-model="user.needsPasswordUpdate"
                        :label="user.needsPasswordUpdate?'Sí':'No'"
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <v-divider></v-divider>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <span class="subtitle-1">Roles:</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="6">
                  <v-autocomplete
                    v-model="user.roles"
                    :items="allRoles"
                    :error-messages="rolesErrors"
                    chips
                    deletable-chips
                    color
                    label="Selecciona los roles del usuario"
                    item-text="name"
                    item-value="id"
                    multiple
                    outlined
                    @blur="$v.user.roles.$touch()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { RolesRepository, UserRepository } from "@/api/Repositories";
import Util from "@/util";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  mixins: [validationMixin],
  validations: {
    user: {
      roles: { required },
    },
  },
  // Props
  props: {},
  // Data
  data: () => ({
    authUser: false,
    isLoadingData: false,
    valid: false,
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "Usuarios", disabled: true }
      ]
    },
    user: {
      id: null,
      documentId: null,
      firstName: null,
      lastName1: null,
      lastName2: null,
      email: null,
      userName: null,
      password: null,
      isActive: true,
      needsPasswordUpdate: false,
      roles: []
    },
    showPassword: false,
    allRoles: [],
    fieldRules: {
      required: v => (!!v && !!v.trim()) || "Obligatorio.",
      emailFormat: v =>
        !v || /.+@.+\..+/.test(v) || "El formato del email no es correcto."
    }
  }),
  // Computed properties
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    isCreating: function() {
      return !this.user.id;
    },
    rolesErrors () {
      const errors = []
      if (!this.$v.user.roles.$dirty) return errors
      !this.$v.user.roles.required && errors.push('Obligatorio.')
      return errors
    }
  },
  // Lifecycle hooks
  methods: {
    loadUsers() {
      this.isLoadingData = true
      this.$store
        .dispatch("users/getAllUsers")
        .then(() => {
          this.loadCurrentUser()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los usuarios."
            Util.showErrorToast(this, error, errorToastMessage);
          }
        })
    },
    loadCurrentUser() {
      var id = this.$route.params.id;
      RolesRepository.getAll().then(res => (this.allRoles = res.data.data));
      if (id) {
        //loading data from server
        UserRepository.get(id)
          .then(res => {
            let tempUser = Util.convertUserFromApiModelToFrontModel(res.data.data);
            tempUser.roles = tempUser.roles.map(r => r.id);
            this.user = tempUser;
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando el usuario."
              Util.showErrorToast(this, error, errorToastMessage);
            }
          })
      } else {
        this.user = {
          id: null,
          documentId: null,
          firstName: null,
          lastName1: null,
          lastName2: null,
          email: null,
          userName: null,
          password: null,
          isActive: true,
          needsPasswordUpdate: true,
          roles: []
        }
      }
      this.authUser = true
      this.isLoadingData = false
      this.$v.$reset()
    },
    save() {
      if (this.$refs.form.validate()) {
        let action = this.isCreating ? "addUser" : "editUser";
        let successToastMessage = this.isCreating
          ? "Usuario añadido correctamente."
          : "Usuario modificado correctamente.";
        let errorToastMessage = this.isCreating
          ? "Ha ocurrido un error añadiendo el usuario."
          : "Ha ocurrido un error modificando el usuario.";

        this.loading = true
        this.$store.dispatch("users/" + action, this.user)
          .then(() => {
            this.$router.push({ name: "user-list" });
            this.$toast(successToastMessage, {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information",
              showClose: true,
              closeText: "Cerrar"
            });
          })
          .catch(error => {
            if (error.response.status === 403) {
              this.$toast(
                "Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.",
                {
                  color: "error",
                  x: "center",
                  y: "top",
                  icon: "mdi-information",
                  timeout: 0,
                  showClose: true,
                  closeText: "Cerrar"
                }
              );
              this.$router.push({ name: "home" });
            } else {
              Util.showErrorToast(this, error, errorToastMessage);
            }
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    validatePasswordRule(p) {
      if (this.isCreating) {
        return (
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/.test(p) ||
          "El formato de la contraseña no es correcto. Debe tener entre 6 y 30 caracteres. Debe contener al menos una mayúscula, una minúscula y un número."
        );
      } else {
        return (
          !p ||
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/.test(p) ||
          "El formato de la contraseña no es correcto. Debe tener entre 6 y 30 caracteres. Debe contener al menos una mayúscula, una minúscula y un número."
        );
      }
    }
  },

  // Lifecycle hooks
  created () {
    this.loadUsers();
  }
};
</script>
